import React, { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";
import { workerAPI } from "../../services/api";
import { useNotification } from "../../hooks/useNotification";

const WorkerList = () => {
  const { t, i18n } = useTranslation();
  const { showSuccess, showError, showWarning } = useNotification();
  const isRTL = i18n.language === "ar";
  const [workers, setWorkers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [filterActive, setFilterActive] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage] = useState(10);

  useEffect(() => {
    loadWorkers();
  }, [search, filterActive]);

  const loadWorkers = async () => {
    try {
      setLoading(true);
      const params = { search };
      if (filterActive !== "") {
        params.is_active = filterActive === "true";
      }
      const response = await workerAPI.getAll(params);
      setWorkers(response.data.data);
      setCurrentPage(1);
    } catch (error) {
      console.error("Error loading workers:", error);
      showError(t("messages.error"));
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm(t("messages.confirmDelete"))) return;
    try {
      await workerAPI.delete(id);
      loadWorkers();
    } catch (error) {
      console.error("Error deleting worker:", error);
      showError(t("messages.error"));
    }
  };

  // Pagination logic
  const indexOfLastItem = currentPage * itemsPerPage;
  const indexOfFirstItem = indexOfLastItem - itemsPerPage;
  const currentItems = workers.slice(indexOfFirstItem, indexOfLastItem);
  const totalPages = Math.ceil(workers.length / itemsPerPage);

  const paginate = (pageNumber) => setCurrentPage(pageNumber);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div>
          <h2 className="text-3xl font-bold text-gray-900">
            {t("workers.title")}
          </h2>
          <p className="text-gray-600 mt-2">{t("workers.subtitle")}</p>
        </div>
        <Link
          to="/workers/new"
          className="flex items-center px-4 py-2 bg-gradient-to-r bg-gray-900 text-white rounded-lg hover:bg-yellow-500  transition-all duration-200 shadow-lg hover:shadow-xl"
        >
          <svg
            className="h-5 w-5 mr-2"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M12 4v16m8-8H4"
            />
          </svg>
          {t("workers.addWorker")}
        </Link>
      </div>

      {/* Main Card */}
      <div className="bg-white rounded-xl shadow-md overflow-hidden">
        {/* Filters */}
        <div className="p-6 border-b border-gray-200">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {/* Search */}
            <div className="relative">
              <svg
                className="absolute left-3 top-3.5 h-5 w-5 text-gray-400"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
                />
              </svg>
              <input
                type="text"
                placeholder={t("common.search")}
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className="w-full pl-10 pr-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all duration-200"
              />
            </div>

            {/* Status Filter */}
            <select
              value={filterActive}
              onChange={(e) => setFilterActive(e.target.value)}
              className="px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all duration-200"
            >
              <option value="">
                {t("common.all")} {t("common.status")}
              </option>
              <option value="true">{t("workers.status.active")}</option>
              <option value="false">{t("workers.status.inactive")}</option>
            </select>
          </div>
        </div>

        {/* Table */}
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-gray-50">
              <tr>
                <th
                  className={`px-6 py-4 ${
                    isRTL ? "text-right" : "text-left"
                  } text-xs font-semibold text-gray-700 uppercase tracking-wider`}
                >
                  {t("workers.fullName")}
                </th>
                <th
                  className={`px-6 py-4 ${
                    isRTL ? "text-right" : "text-left"
                  } text-xs font-semibold text-gray-700 uppercase tracking-wider`}
                >
                  {t("workers.skills")}
                </th>
                <th
                  className={`px-6 py-4 ${
                    isRTL ? "text-right" : "text-left"
                  } text-xs font-semibold text-gray-700 uppercase tracking-wider`}
                >
                  {t("workers.hourlyRate")}
                </th>
                <th
                  className={`px-6 py-4 ${
                    isRTL ? "text-right" : "text-left"
                  } text-xs font-semibold text-gray-700 uppercase tracking-wider`}
                >
                  {t("common.status")}
                </th>
                <th
                  className={`px-6 py-4 ${
                    isRTL ? "text-right" : "text-left"
                  } text-xs font-semibold text-gray-700 uppercase tracking-wider`}
                >
                  {t("common.actions")}
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200">
              {currentItems.length === 0 ? (
                <tr>
                  <td
                    colSpan="5"
                    className="px-6 py-8 text-center text-gray-500"
                  >
                    <div className="flex flex-col items-center justify-center">
                      <svg
                        className="h-12 w-12 text-gray-400 mb-2"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M20 13V6a2 2 0 00-2-2H6a2 2 0 00-2 2v7m16 0v5a2 2 0 01-2 2H6a2 2 0 01-2-2v-5m16 0h-2.586a1 1 0 00-.707.293l-2.414 2.414a1 1 0 01-.707.293h-3.172a1 1 0 01-.707-.293l-2.414-2.414A1 1 0 006.586 13H4"
                        />
                      </svg>
                      {t("common.noData")}
                    </div>
                  </td>
                </tr>
              ) : (
                currentItems.map((worker) => (
                  <tr
                    key={worker.id}
                    className="hover:bg-gray-50 transition-colors duration-150"
                  >
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div
                        className={`flex items-center gap-3 ${
                          isRTL ? "flex-row-reverse" : ""
                        }`}
                      >
                        <div className="h-10 w-10 bg-blue-100 rounded-full flex items-center justify-center">
                          <span className="text-blue-700 font-semibold text-sm">
                            {worker.full_name?.charAt(0)?.toUpperCase()}
                          </span>
                        </div>
                        <div className={isRTL ? "text-right" : "text-left"}>
                          <div className="text-sm font-medium text-gray-900">
                            {worker.full_name}
                          </div>
                          <div className="text-xs text-gray-500">
                            {worker.email}
                          </div>
                        </div>
                      </div>
                    </td>
                    <td
                      className={`px-6 py-4 ${
                        isRTL ? "text-right" : "text-left"
                      }`}
                    >
                      <div
                        className={`flex flex-wrap gap-1 ${
                          isRTL ? "justify-end" : "justify-start"
                        }`}
                      >
                        {worker.skills &&
                          worker.skills
                            .split(",")
                            .slice(0, 2)
                            .map((skill, idx) => (
                              <span
                                key={idx}
                                className="px-2.5 py-1 text-xs bg-blue-100 text-blue-700 rounded-full font-medium"
                              >
                                {skill.trim()}
                              </span>
                            ))}
                        {worker.skills &&
                          worker.skills.split(",").length > 2 && (
                            <span className="px-2.5 py-1 text-xs bg-gray-100 text-gray-600 rounded-full font-medium">
                              +{worker.skills.split(",").length - 2}
                            </span>
                          )}
                      </div>
                    </td>
                    <td
                      className={`px-6 py-4 whitespace-nowrap ${
                        isRTL ? "text-right" : "text-left"
                      }`}
                    >
                      <div className="text-sm font-medium text-gray-900">
                        AED {parseFloat(worker.hourly_rate).toFixed(2)}
                      </div>
                    </td>
                    <td
                      className={`px-6 py-4 whitespace-nowrap ${
                        isRTL ? "text-right" : "text-left"
                      }`}
                    >
                      <span
                        className={`px-3 py-1.5 inline-flex text-xs leading-5 font-semibold rounded-full transition-all duration-200 ${
                          worker.is_active
                            ? "bg-green-100 text-green-700"
                            : "bg-gray-100 text-gray-600"
                        }`}
                      >
                        {worker.is_active
                          ? t("workers.status.active")
                          : t("workers.status.inactive")}
                      </span>
                    </td>
                    <td
                      className={`px-6 py-4 whitespace-nowrap ${
                        isRTL ? "text-right" : "text-left"
                      }`}
                    >
                      <div
                        className={`flex items-center gap-2 ${
                          isRTL ? "justify-end" : "justify-start"
                        }`}
                      >
                        <Link
                          to={`/workers/${worker.id}`}
                          className="p-2 text-gray-600 hover:text-blue-600 hover:bg-blue-50 rounded-lg transition-colors"
                          title={t("common.viewDetails")}
                        >
                          <svg
                            className="w-5 h-5"
                            fill="none"
                            stroke="currentColor"
                            viewBox="0 0 24 24"
                          >
                            <path
                              strokeLinecap="round"
                              strokeLinejoin="round"
                              strokeWidth={2}
                              d="M15 12a3 3 0 11-6 0 3 3 0 016 0z"
                            />
                            <path
                              strokeLinecap="round"
                              strokeLinejoin="round"
                              strokeWidth={2}
                              d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z"
                            />
                          </svg>
                        </Link>
                        <Link
                          to={`/workers/${worker.id}/edit`}
                          className="p-2 text-gray-600 hover:text-green-600 hover:bg-green-50 rounded-lg transition-colors"
                          title={t("common.edit")}
                        >
                          <svg
                            className="w-5 h-5"
                            fill="none"
                            stroke="currentColor"
                            viewBox="0 0 24 24"
                          >
                            <path
                              strokeLinecap="round"
                              strokeLinejoin="round"
                              strokeWidth={2}
                              d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z"
                            />
                          </svg>
                        </Link>
                        <button
                          onClick={() => handleDelete(worker.id)}
                          className="p-2 text-gray-600 hover:text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                          title={t("common.delete")}
                        >
                          <svg
                            className="w-5 h-5"
                            fill="none"
                            stroke="currentColor"
                            viewBox="0 0 24 24"
                          >
                            <path
                              strokeLinecap="round"
                              strokeLinejoin="round"
                              strokeWidth={2}
                              d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"
                            />
                          </svg>
                        </button>
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        {/* Pagination */}
        {totalPages > 1 && (
          <div className="px-6 py-4 border-t border-gray-200 flex items-center justify-between">
            <div className="text-sm text-gray-600">
              {t("common.showing")} {indexOfFirstItem + 1} {t("common.to")}{" "}
              {Math.min(indexOfLastItem, workers.length)} {t("common.of")}{" "}
              {workers.length}
            </div>
            <div className="flex space-x-2">
              {Array.from({ length: totalPages }, (_, i) => i + 1).map(
                (page) => (
                  <button
                    key={page}
                    onClick={() => paginate(page)}
                    className={`px-3 py-1 rounded ${
                      currentPage === page
                        ? "bg-blue-600 text-white"
                        : "bg-gray-200 text-gray-700 hover:bg-gray-300"
                    }`}
                  >
                    {page}
                  </button>
                )
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default WorkerList;
